import type { LocationCues, MessageSuggestion } from "@expert/shared-types";

import { type GaiaWebSocketResponse, gaiaWsEventBus } from "@expert/gaia";
import { useEffect } from "react";

import { getRootDispatcher } from "@/analytics";
import { env } from "@/config";
import { useDetectedCuesStore } from "@/sales-tools/detected-cues/stores/DetectedCuesStore";
import { useOfferChecklistStore } from "@/sales-tools/home-product/stores/useOfferChecklistStore";
import { type ChatTask, isChatTask } from "@/sdk/agent/chat";
import { useLogger } from "@/sdk/logging";
import { useSessionStore } from "@/sdk/sessions/session.store";

export const useTechCuesListener = () => {
    const logger = useLogger({ module: "useTechCuesListener" });
    useEffect(() => {
        const subscription = gaiaWsEventBus.on("gaia_ws_message-suggestion", ({ body }: GaiaWebSocketResponse) => {
            const gaiaSalesMessage = body as MessageSuggestion;
            const cues = gaiaSalesMessage.suggestion.cues;

            if (!cues) {
                return;
            }

            const exwoSessionId = getExWoSessionIdFromGaiaSessionGroupId(gaiaSalesMessage.sessionGroupId);

            if (!exwoSessionId) {
                logger.warn(
                    { gaiaSessionGroupId: gaiaSalesMessage.sessionGroupId, gaiaSessionId: gaiaSalesMessage.sessionId },
                    "No Exwo session id found for GAIA message, skipping tech cues processing",
                );
                return;
            }

            // Process device and software cues
            const techCues = cues.filter((cue) => cue.type === "DEVICE" || cue.type === "SOFTWARE");
            if (techCues.length > 0) {
                techCues.forEach((cue) => {
                    void getRootDispatcher().dispatchBusinessEvent("CustomerCueDetected", {
                        detectionSource: "GAIA",
                        cueType: cue.type,
                        cueName: cue.name,
                    });
                });

                useDetectedCuesStore.getState().processNewlyDetectedCue(exwoSessionId, techCues);
                useOfferChecklistStore.getState().setData(exwoSessionId, { DEVICE_CUE: true });
            }

            // Process location cues
            const locationCues = cues.filter(
                (cue): cue is LocationCues => cue.type === "LOCATION_STATE" || cue.type === "LOCATION_CITY",
            );
            if (locationCues.length > 0) {
                const state = locationCues.find((cue) => cue.type === "LOCATION_STATE")?.name;
                const city = locationCues.find((cue) => cue.type === "LOCATION_CITY")?.name;

                useDetectedCuesStore.getState().processLocationCues(exwoSessionId, locationCues);

                void getRootDispatcher().dispatchBusinessEvent("LocationCuesReceived", {
                    detectionSource: "GAIA",
                    state,
                    city,
                    partner: gaiaSalesMessage.partner,
                    sessionGroupId: gaiaSalesMessage.sessionGroupId,
                    sessionId: gaiaSalesMessage.sessionId,
                });
            }
        });
        return () => subscription();
    }, []);
};

/**
 * Given a GAIA session group ID, return the related ExWo session ID.
 * This is needed because GAIA sessionGroupId for device cue events is the chat ID rather than the session ID.
 *
 * @param sessionGroupId - The session group ID from GAIA (either the ExWo session ID or the chat ID)
 * @returns The ExWo session ID
 */
function getExWoSessionIdFromGaiaSessionGroupId(sessionGroupId: string) {
    if (env.channel !== "chat") {
        return sessionGroupId;
    }

    const sessions = useSessionStore.getState().sessions;
    const sessionIds = sessions.map((session) => session.id);

    if (sessionGroupId in sessionIds) {
        return sessionGroupId;
    }

    return sessions
        .filter((session) => isChatTask(session.currentTask))
        .find((session) => (session.currentTask as ChatTask)?.chatId === sessionGroupId)?.id;
}
